/**
 * Stage Plot composite-element configuration normalization.
 *
 * SINGLE-SOURCE normalization for composite stage elements whose contents are
 * described by a structured `config` (risers / spd / drumkit). Canvas rendering
 * and every add/load path must go through a valid config: a composite with a
 * missing or invalid config used to crash the renderer ("reading 'uniform'").
 *
 * RULE: a composite element (iconId in {risers, spd, drumkit}) is only
 * renderable when it carries a VALID config for its iconId. Every element that
 * enters the editor (deserialization, generated draft, AI mapping, palette
 * add) MUST be run through `normalizeElementConfig`. Valid configs are kept
 * verbatim; missing/invalid ones fall back to the iconId default so the
 * renderer never crashes and the user can still open the editor to refine.
 *
 * Every other iconId is returned unchanged (plain elements need no config).
 */

import type { StageElement } from '../shared/types/stageplot';
import { defaultRisers, type RiserConfig } from '../components/StagePlotEditor/riserConfig';
import { defaultSpd, type SpdConfig } from '../components/StagePlotEditor/spdConfig';
import { defaultDrumKit, type DrumKitConfig } from '../components/StagePlotEditor/drumKitConfig';

export function isValidRiserConfig(cfg: unknown): cfg is RiserConfig {
  return (
    !!cfg &&
    typeof cfg === 'object' &&
    typeof (cfg as RiserConfig).count === 'number' &&
    typeof (cfg as RiserConfig).uniform === 'boolean' &&
    !!((cfg as RiserConfig).size) &&
    typeof (cfg as RiserConfig).size.width === 'number'
  );
}

export function isValidSpdConfig(cfg: unknown): cfg is SpdConfig {
  return !!cfg && typeof cfg === 'object' && typeof (cfg as SpdConfig).onStand === 'boolean';
}

export function isValidDrumKitConfig(cfg: unknown): cfg is DrumKitConfig {
  return (
    !!cfg &&
    typeof cfg === 'object' &&
    !!(cfg as DrumKitConfig).counts &&
    typeof (cfg as DrumKitConfig).counts === 'object' &&
    typeof (cfg as DrumKitConfig).handedness === 'string'
  );
}

/**
 * Normalize a single element's composite config. Returns an element that is
 * safe to render: composites get a valid default config when theirs is missing
 * or invalid; a VALID existing config is preserved verbatim; all other
 * iconIds pass through unchanged.
 */
export function normalizeElementConfig(el: StageElement): StageElement {
  if (el.iconId === 'risers') {
    if (isValidRiserConfig(el.config)) return el;
    return { ...el, config: defaultRisers() as unknown as Record<string, unknown> };
  }
  if (el.iconId === 'spd') {
    if (isValidSpdConfig(el.config)) return el;
    return { ...el, config: defaultSpd() as unknown as Record<string, unknown> };
  }
  if (el.iconId === 'drumkit') {
    if (isValidDrumKitConfig(el.config)) return el;
    return { ...el, config: defaultDrumKit() as unknown as Record<string, unknown> };
  }
  return el;
}

/** Normalize every composite element in a plot's element list (safe to map). */
export function normalizePlotElementConfigs(elements: StageElement[]): StageElement[] {
  return elements.map(normalizeElementConfig);
}